import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { Tabs } from '@pms-digital/design-system';
import type { ReactNode } from 'react';

/**
 * Les deux onglets de l'écran Sessions : **Liste** et **Matière** (§12.1,
 * §12.4, §15).
 *
 * Ils s'appellent ainsi et pas « Sessions » et « Matière » : « "Sessions"
 * répété en onglet sous un `h1` "Sessions" dirait deux fois la même chose »
 * (§15). Ce sont les titres mêmes des deux sections de la spec.
 *
 * ## Les onglets du package
 *
 * Le choix voyage dans l'URL (`?tab=`), donc un rechargement, un retour arrière
 * ou un lien collé retombent sur le même panneau. La barre était un `nav` à
 * `aria-current` ; elle est depuis la migration le `Tabs` contrôlé du design
 * system (`role="tab"`, `aria-selected`, flèches), pour se lire comme les
 * onglets de toutes les applications PMS. Ce sont des onglets sans lien et sans
 * panneau : ce composant est aveugle à la route qui le monte, TanStack type
 * `to` et `search` contre l'arbre de routes, donc la navigation part par un
 * rappel et c'est l'écran qui l'écrit, puis rend lui-même le panneau.
 *
 * Le `Tabs` du package ne prend pas de `data-testid` : la marque de la barre
 * est sur son enveloppe, celle d'un onglet sur son libellé.
 *
 * ## One bar, two screens (#172)
 *
 * The parcours of a session carries the same bar under its eight figures -
 * Parcours and Variables, « une barre au patron Liste / Matière » (§12.2). So
 * the bar itself is `TabBar`, generic over its tabs and its marks, and
 * `SessionsTabs` is the list's instance of it: two copies of this wiring
 * would drift on the first retouch of either.
 */

export const SESSIONS_TABS = [
  { id: 'liste', label: 'Liste' },
  { id: 'matiere', label: 'Matière' },
] as const;

export type SessionsTab = (typeof SESSIONS_TABS)[number]['id'];

/** One tab of a bar: the value its screen writes, and the word it shows. */
export interface TabBarItem<T extends string> {
  readonly id: T;
  readonly label: string;
}

export interface TabBarProps<T extends string> {
  readonly tabs: readonly TabBarItem<T>[];
  readonly active: T;
  readonly onSelect: (tab: T) => void;
  /** The mark of the bar, on its envelope. */
  readonly testId: string;
  /** The mark of each tab, on its label, suffixed with `-<id>`. */
  readonly tabTestId: string;
  /**
   * Something that stands on the line of the tabs, at its right (#213: the search bar of
   * Sessions). Below 768 px it drops UNDER the tabs. The bar then lends its rule to the
   * wrapper, so the tabs keep theirs.
   */
  readonly trailing?: ReactNode;
}

export function TabBar<T extends string>({
  tabs,
  active,
  onSelect,
  testId,
  tabTestId,
  trailing,
}: TabBarProps<T>) {
  const bar = (
    <div data-testid={testId} className={trailing === undefined ? undefined : 'min-w-0 md:flex-1'}>
      <Tabs
        label="Sections"
        value={active}
        onValueChange={(value) => {
          const tab = tabs.find((candidate) => candidate.id === value);
          if (tab !== undefined) onSelect(tab.id);
        }}
        items={tabs.map((tab) => ({
          value: tab.id,
          label: <span data-testid={`${tabTestId}-${tab.id}`}>{tab.label}</span>,
        }))}
      />
    </div>
  );
  if (trailing === undefined) return bar;
  // The tabs keep their own rule. From 768 px the field stands at their right, its foot on the
  // line of the tabs (#213); below, it drops UNDER them, full width - not the package `trailing`,
  // which keeps it inside the tabs' row, above their rule, at the width of its content.
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
      {bar}
      {trailing}
    </div>
  );
}

export interface SessionsTabsProps {
  readonly active: SessionsTab;
  readonly onSelect: (tab: SessionsTab) => void;
  /** The search bar of §4 (#213), shared by both tabs. */
  readonly trailing?: ReactNode;
}

export function SessionsTabs({ active, onSelect, trailing }: SessionsTabsProps) {
  return (
    <TabBar
      tabs={SESSIONS_TABS}
      active={active}
      onSelect={onSelect}
      testId={SESSIONS_TEST_IDS.tabs}
      tabTestId="sessions-onglet"
      trailing={trailing}
    />
  );
}
